import { FunctionComponent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ProcessRunState, ProcessRunResult } from '@robocorp/robocloud-orchestrator-interface';
import { Badge, BadgeProps, BadgeVariant, Tooltip } from '@robocorp/components';
import {
  IconExpand,
  IconStatusCompleted,
  IconStatusError,
  IconStatusNew,
  IconStatusPending,
  IconStatusTerminated,
  IconStatusTimeout,
  IconStatusUnresolved,
  IconType,
} from '@robocorp/icons';
import { Color } from '@robocorp/theme';
import { IconLoading } from '@robocorp/icons/animated';

import { AssistantRunResult, AssistantRunState } from '~/types/resources';

type OwnProps = {
  state?: `${ProcessRunState}` | `${AssistantRunState}`;
  result?: `${ProcessRunResult}` | `${AssistantRunResult}`;
  errorCode?: string;
  label?: ReactNode;
  forwardedAs?: 'button';
} & Pick<BadgeProps, 'size' | 'iconAfter'>;

export const runVariants: Record<
  ProcessRunResult | ProcessRunState,
  {
    variant: BadgeVariant;
    icon: IconType;
    iconColor: Color;
  }
> = {
  [ProcessRunResult.Timeout]: {
    variant: 'primary',
    icon: IconStatusTimeout,
    iconColor: 'content.subtle',
  },
  [ProcessRunResult.StartFailed]: {
    variant: 'danger',
    icon: IconStatusError,
    iconColor: 'background.error',
  },
  [ProcessRunResult.ConnectionLoss]: {
    variant: 'danger',
    icon: IconStatusError,
    iconColor: 'background.error',
  },
  [ProcessRunResult.Error]: {
    variant: 'danger',
    icon: IconStatusError,
    iconColor: 'background.error',
  },
  [ProcessRunResult.Terminated]: {
    variant: 'danger',
    icon: IconStatusError,
    iconColor: 'background.error',
  },
  [ProcessRunResult.Ok]: {
    variant: 'success',
    icon: IconStatusCompleted,
    iconColor: 'background.success',
  },
  [ProcessRunState.New]: {
    variant: 'primary',
    icon: IconStatusNew,
    iconColor: 'content.subtle',
  },
  [ProcessRunState.InProgress]: {
    variant: 'primary',
    icon: IconLoading,
    iconColor: 'content.subtle',
  },
  [ProcessRunState.Pending]: {
    variant: 'danger',
    icon: IconStatusUnresolved,
    iconColor: 'background.error',
  },
  [ProcessRunState.Stopping]: {
    variant: 'yellow',
    icon: IconStatusTerminated,
    iconColor: 'background.notification',
  },
  [ProcessRunState.Complete]: {
    variant: 'success',
    icon: IconStatusCompleted,
    iconColor: 'background.success',
  },
};

const RunAvatar: FunctionComponent<OwnProps> = ({
  errorCode,
  result,
  state,
  label: defaultLabel,
  ...rest
}) => {
  const { t } = useTranslation();

  const variantKey = result || state;

  const { icon, iconColor, variant } = variantKey
    ? runVariants[variantKey]
    : {
        variant: 'primary' as BadgeVariant,
        icon: IconStatusPending,
        iconColor: 'content.subtle' as Color,
      };

  const label = result ? t(`processRunResult.${result}`) : t(`processRunState.${state}`);

  return (
    <Tooltip text={errorCode}>
      <Badge
        icon={icon}
        iconColor={iconColor}
        variant={variant}
        label={defaultLabel || label}
        {...rest}
        iconAfter={IconExpand}
      />
    </Tooltip>
  );
};

export default RunAvatar;
